'use client';

import { type PropsWithChildren, useMemo } from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { CreateCollectionProps } from '../hooks/useCreateCollection/useCreateCollection';
import { useHandleCreateCollectionAndRedirect } from './hooks/useHandleCreateCollectionAndRedirect';

type CollectionOperationsValue = {
  createCollectionAndRedirect: (props: CreateCollectionProps) => void;
  isCreateCollectionLoading: boolean;
};

export const CollectionOperationsContext =
  createContext<CollectionOperationsValue>({
    createCollectionAndRedirect: noop,
    isCreateCollectionLoading: false,
  });

/**
 * The CollectionOperationsContextProvider manages state needed to support select collection operations.
 */
export function CollectionOperationsContextProvider({
  children,
}: PropsWithChildren) {
  const { createCollectionAndRedirect, isLoading } =
    useHandleCreateCollectionAndRedirect();

  const contextValue = useMemo<CollectionOperationsValue>(
    () => ({
      createCollectionAndRedirect,
      isCreateCollectionLoading: isLoading,
    }),
    [createCollectionAndRedirect, isLoading]
  );

  return (
    <CollectionOperationsContext.Provider value={contextValue}>
      <>{children}</>
    </CollectionOperationsContext.Provider>
  );
}

/**
 * useCreateCollectionAndRedirect provides a callback which handles redirecting to the newly created collection if successful.
 *
 * @returns createCollectionAndRedirect - a function that creates a collection and redirects to created collection
 * @returns isLoading - a boolean indicating whether the operation is in progress
 */
export function useCreateCollectionAndRedirect() {
  const createCollectionAndRedirect = useContextSelector(
    CollectionOperationsContext,
    (value) => value.createCollectionAndRedirect
  );

  const isLoading = useContextSelector(
    CollectionOperationsContext,
    (value) => value.isCreateCollectionLoading
  );

  return { createCollectionAndRedirect, isLoading };
}
